import React from 'react';

import ThreeDots from 'icons/ThreeDots';
import LoadingSpinner from 'icons/LoadingSpinner';

type MoreButtonSize = 'sm' | 'md' | 'default';
type MoreButtonVariant = 'bordered' | 'plain';

// Heights are declared rather than left to padding plus line-height, so a loading spinner cannot
// resize the control and `default` lines up with the 40px buttons it sits beside in page headers.
const sizeClasses: Record<MoreButtonSize, string> = {
  sm: 'h-7 px-3 py-1',
  md: 'h-9 px-3 py-2',
  default: 'h-10 px-3.5 py-2.5',
};

const variantClasses: Record<MoreButtonVariant, string> = {
  bordered: 'rounded-md border border-gray-300',
  plain: 'bg-transparent hover:opacity-70',
};

const MoreButton = React.forwardRef<
  HTMLButtonElement,
  {
    size?: MoreButtonSize;
    variant?: MoreButtonVariant;
    className?: string;
    isLoading?: boolean;
    disabled?: boolean;
    onClick?: (e: React.MouseEvent) => void;
  }
>(
  (
    { size = 'default', variant = 'bordered', className, isLoading = false, disabled = false, onClick, ...rest },
    ref,
  ) => {
    const baseVariant = variantClasses[variant];
    // Always apply the size-based padding + hover styles; `className` is additive on top
    // (escape-hatch overrides win because Tailwind is order-stable).
    const variantPadding = variant === 'bordered' ? `${sizeClasses[size]} hover:bg-gray-50` : sizeClasses[size];
    const padding = `${variantPadding} ${className ?? ''}`.trim();

    return (
      <button
        ref={ref}
        type="button"
        {...rest}
        className={`flex shrink-0 items-center outline-none focus-visible:ring-2 focus-visible:ring-therablue focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ${baseVariant} ${padding}`}
        aria-label="More"
        disabled={disabled}
        onClick={onClick}
      >
        {isLoading ? (
          <LoadingSpinner width={16} height={16} />
        ) : (
          <>
            <span className="mr-2 text-sm font-medium text-neutral-600">More</span>
            <ThreeDots className="h-3 w-3" />
          </>
        )}
      </button>
    );
  },
);

MoreButton.displayName = 'MoreButton';

export default MoreButton;
